type EventParamValue = string | number | boolean;

const DEFAULT_MEASUREMENT_ID = 'G-B24EHDVM89';
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || DEFAULT_MEASUREMENT_ID;
const ANALYTICS_ENABLED = Boolean(MEASUREMENT_ID) && import.meta.env.MODE !== 'test';
const GA_DEBUG_MODE = import.meta.env.VITE_GA_DEBUG_MODE !== 'false';

let analyticsInitialized = false;

const EVENT_ALLOWLIST: Record<string, readonly string[]> = {
  page_view: ['page_path', 'page_location', 'page_title'],
  component_viewed: ['component', 'page', 'broker_id', 'account_id', 'account_type', 'chain', 'status'],
  modal_opened: ['modal', 'step', 'component', 'account_id', 'broker_id', 'account_type'],
  modal_closed: ['modal', 'step', 'component', 'account_id', 'broker_id', 'account_type', 'action'],
  offboarding_execute_clicked: ['step', 'component', 'account_id', 'broker_id', 'account_type', 'token'],
  connect_wallet_click: ['chain', 'wallet_group'],
  dex_list_viewed: ['chain', 'dex_count'],
  dex_selected: ['broker_id', 'chain'],
  account_list_viewed: ['broker_id', 'chain', 'account_count'],
  account_selected: ['account_id', 'account_type', 'broker_id'],
  account_viewed: ['account_id', 'account_type', 'broker_id'],
  offboarding_step_started: ['account_id', 'step', 'account_type', 'action', 'token'],
  offboarding_step_succeeded: ['account_id', 'step', 'account_type', 'action', 'token'],
  offboarding_step_partial: ['account_id', 'step', 'account_type', 'action', 'token', 'usdc_balance'],
  offboarding_step_rejected: ['account_id', 'step', 'account_type', 'action', 'token'],
  offboarding_step_failed: ['account_id', 'step', 'account_type', 'action', 'token'],
};

const ETH_LIKE_HEX_REGEX = /^0x[a-fA-F0-9]{40,}$/;
const BASE58_LONG_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,120}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function ensureGtag(): ((...args: unknown[]) => void) | null {
  if (!ANALYTICS_ENABLED) return null;

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer.push(args);
    };
  }

  return window.gtag;
}

function sanitizeParamValue(key: string, value: EventParamValue): EventParamValue | undefined {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return undefined;

    if (key === 'account_id') {
      return trimmedValue.length > 128 ? trimmedValue.slice(0, 128) : trimmedValue;
    }

    if (
      ETH_LIKE_HEX_REGEX.test(trimmedValue)
      || BASE58_LONG_REGEX.test(trimmedValue)
      || EMAIL_REGEX.test(trimmedValue)
    ) {
      return undefined;
    }

    return trimmedValue.length > 80 ? trimmedValue.slice(0, 80) : trimmedValue;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  return value;
}

function sanitizeEventParams(eventName: string, params: Record<string, EventParamValue | undefined>): Record<string, EventParamValue> {
  const allowedKeys = EVENT_ALLOWLIST[eventName];
  if (!allowedKeys) {
    return {};
  }

  const cleanParams: Record<string, EventParamValue> = {};
  for (const key of allowedKeys) {
    const value = params[key];
    if (value === undefined) continue;

    const sanitizedValue = sanitizeParamValue(key, value);
    if (sanitizedValue !== undefined) {
      cleanParams[key] = sanitizedValue;
    }
  }

  return cleanParams;
}

export function initAnalytics(): void {
  if (!ANALYTICS_ENABLED || analyticsInitialized || !MEASUREMENT_ID) {
    return;
  }

  const existingScript = document.querySelector(`script[src="https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}"]`);
  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  const gtag = ensureGtag();
  if (!gtag) return;

  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID, { send_page_view: false, debug_mode: GA_DEBUG_MODE });
  analyticsInitialized = true;
}

export function trackPageView(pagePath: string): void {
  if (!ANALYTICS_ENABLED) return;
  initAnalytics();

  const gtag = ensureGtag();
  if (!gtag) return;

  gtag('event', 'page_view', {
    debug_mode: GA_DEBUG_MODE,
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(eventName: string, params: Record<string, EventParamValue | undefined> = {}): void {
  if (!ANALYTICS_ENABLED) return;
  if (!EVENT_ALLOWLIST[eventName]) return;

  initAnalytics();

  const gtag = ensureGtag();
  if (!gtag) return;

  const cleanParams = sanitizeEventParams(eventName, params);

  gtag('event', eventName, {
    debug_mode: GA_DEBUG_MODE,
    ...cleanParams,
  });
}
